import { useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export type FieldSpec =
  | { name: string; label: string; type: 'text'; optional?: boolean; lockOnEdit?: boolean; placeholder?: string; hint?: string }
  | { name: string; label: string; type: 'lines'; optional?: boolean; hint?: string; placeholder?: string }
  | { name: string; label: string; type: 'textarea'; optional?: boolean; hint?: string; placeholder?: string }
  | { name: string; label: string; type: 'checkboxGroup'; optional?: boolean; options: { value: string; label: string }[] }
  | { name: string; label: string; type: 'select'; optional?: boolean; hint?: string; options: { value: string; label: string }[] }
  | { name: string; label: string; type: 'json'; optional?: boolean; hint?: string; placeholder?: string };

export function formSubmittable(fields: FieldSpec[], values: Record<string, unknown>): boolean {
  return fields.every((f) => {
    if (f.type === 'json') {
      const raw = String(values[f.name] ?? '').trim();
      if (!raw) return !!f.optional;
      try { JSON.parse(raw); return true; } catch { return false; }
    }
    if (f.optional) return true;
    if (f.type === 'text' || f.type === 'select') return String(values[f.name] ?? '').trim().length > 0;
    return true;
  });
}

export function EntityFormModal({
  title, intro, fields, initialValues, mode, onSubmit, onClose, error, busy,
}: {
  title: string;
  /** Shown under the title, before the fields: what the form is about. */
  intro?: ReactNode;
  fields: FieldSpec[];
  initialValues: Record<string, unknown>;
  mode: 'add' | 'edit';
  onSubmit: (values: Record<string, unknown>) => void;
  onClose: () => void;
  error: string | null;
  busy: boolean;
}) {
  const [values, setValues] = useState<Record<string, unknown>>(initialValues);
  const set = (name: string, v: unknown) => setValues((cur) => ({ ...cur, [name]: v }));

  const submit = () => {
    // normalise 'lines' fields (textarea → string[]) at submit
    const out: Record<string, unknown> = { ...values };
    for (const f of fields) {
      if (f.type === 'lines') out[f.name] = String(values[f.name] ?? '').split('\n').map((s) => s.trim()).filter(Boolean);
      if (f.type === 'json') {
        const raw = String(values[f.name] ?? '').trim();
        out[f.name] = raw ? JSON.parse(raw) : undefined;
      }
    }
    onSubmit(out);
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-overlay" onClick={onClose}>
      <div className="w-[520px] max-w-[90vw] rounded bg-surface p-21" onClick={(e) => e.stopPropagation()}>
        <h3 className="mb-13 text-h3 font-semibold">{title}</h3>
        {intro ? <div className="mb-13">{intro}</div> : null}
        {fields.map((f) => (
          <div key={f.name} className="mb-13">
            <label className="mb-1 block text-body-sm font-medium">{f.label}</label>
            {f.type === 'text' ? (
              <Input value={String(values[f.name] ?? '')} disabled={mode === 'edit' && f.lockOnEdit}
                placeholder={f.placeholder} onChange={(e) => set(f.name, e.target.value)} />
            ) : null}
            {f.type === 'lines' || f.type === 'textarea' ? (
              <textarea
                value={f.type === 'lines' && Array.isArray(values[f.name]) ? (values[f.name] as string[]).join('\n') : String(values[f.name] ?? '')}
                placeholder={f.placeholder} onChange={(e) => set(f.name, e.target.value)}
                className="min-h-[60px] w-full resize-y rounded border border-border-strong bg-bg px-13 py-8 text-body focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
              />
            ) : null}
            {f.type === 'checkboxGroup' ? (
              <div className="flex flex-wrap gap-8">
                {f.options.map((o) => {
                  const arr = (values[f.name] as string[]) ?? [];
                  return (
                    <label key={o.value} className="flex cursor-pointer items-center gap-1 rounded border border-border px-2 py-1 text-[12px] hover:bg-bg">
                      <input type="checkbox" checked={arr.includes(o.value)}
                        onChange={() => set(f.name, arr.includes(o.value) ? arr.filter((x) => x !== o.value) : [...arr, o.value])} />
                      {o.label}
                    </label>
                  );
                })}
              </div>
            ) : null}
            {f.type === 'json' ? (
              <>
                <textarea
                  value={String(values[f.name] ?? '')} placeholder={f.placeholder}
                  onChange={(e) => set(f.name, e.target.value)} spellCheck={false}
                  className="min-h-[280px] w-full resize-y rounded border border-border-strong bg-bg px-13 py-8 font-mono text-[12px] leading-snug focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
                />
                {String(values[f.name] ?? '').trim() && !(() => { try { JSON.parse(String(values[f.name])); return true; } catch { return false; } })()
                  ? <div className="mt-1 text-[11px] text-flagged">Invalid JSON</div> : null}
              </>
            ) : null}
            {f.type === 'select' ? (
              <select value={String(values[f.name] ?? '')} onChange={(e) => set(f.name, e.target.value)}
                className="w-full rounded border border-border-strong bg-bg px-13 py-2 text-body">
                <option value=""></option>
                {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : null}
            {'hint' in f && f.hint ? <div className="mt-1 text-[11px] text-text-secondary">{f.hint}</div> : null}
          </div>
        ))}
        <div className="flex justify-end gap-8">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" disabled={busy || !formSubmittable(fields, values)} onClick={submit}>{busy ? 'Saving…' : 'Save'}</Button>
        </div>
        {error ? <p className="mt-8 text-body-sm text-flagged">{error}</p> : null}
      </div>
    </div>
  );
}
